import React from 'react';
import { useTranslation } from 'react-i18next';

import CustomInput from '../../../shared-components/custom-input/CustomInput';
import RadioButton from '../../../shared-components/radio-button/RadioButton';
import ToFixedValue from '../../../shared-functions/ToFixedValue';
import type { EnergyCostType } from '../../../utils/api/revenue';

interface UtilityCostDetailsProperties {
  energyCostType?: EnergyCostType;
  energyCostEnabled?: boolean;
  bearingOption?: 'partner' | 'cpo' | 'none';
  currency?: string;
  partnerPercentage?: number | null;
  cpoPercentage?: number | null;
}

/**
 * Coin stack icon representing shareable amount.
 * @returns {any} The rendered SVG component.
 */
function CoinsIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round">
      <ellipse cx="12" cy="6" rx="8" ry="3" />
      <path d="M4 6v6c0 1.66 3.58 3 8 3s8-1.34 8-3V6" />
      <path d="M4 12v6c0 1.66 3.58 3 8 3s8-1.34 8-3v-6" />
    </svg>
  );
}

/**
 * Utility plug icon representing utility provider cost.
 * @returns {any} The rendered SVG component.
 */
function UtilityPlugIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round">
      <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
    </svg>
  );
}

/**
 * Partner user icon representing partner revenue share.
 * @returns {any} The rendered SVG component.
 */
function PartnerUserIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round">
      <circle cx="12" cy="7" r="4" />
      <path d="M5.5 21a8.38 8.38 0 0 1 13 0" />
    </svg>
  );
}

/**
 * Bank/institution icon representing CPO revenue share.
 * @returns {any} The rendered SVG component.
 */
function CpoBankIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round">
      <path d="M3 21h18" />
      <path d="M3 10h18" />
      <path d="M5 6l7-3 7 3" />
      <path d="M4 10v11" />
      <path d="M20 10v11" />
      <path d="M8 14v3" />
      <path d="M12 14v3" />
      <path d="M16 14v3" />
    </svg>
  );
}

/**
 * Info icon for the sample data explanation banner.
 * @returns {any} The rendered SVG component.
 */
function InfoIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="16" x2="12" y2="12" />
      <line x1="12" y1="8" x2="12.01" y2="8" />
    </svg>
  );
}

/**
 * Helper to format an amount with a dynamic currency symbol or code.
 * @param {number} amount - The amount to format.
 * @param {string} [currency] - The currency code (e.g. INR, USD, SAR).
 * @returns {string} The formatted currency string.
 */
const formatCurrencyDisplay = (amount: number, currency = 'SAR'): string => {
  const symbolMap: Record<string, string> = {
    INR: '₹',
    USD: '$',
    EUR: '€',
    GBP: '£',
  };
  const upperCurrency = currency ? currency.toUpperCase() : 'SAR';
  const symbol = symbolMap[upperCurrency] || currency || 'SAR';
  if (symbol.length === 1 || symbol === '₹') {
    return `${ToFixedValue(amount)}${symbol}`;
  }
  return `${ToFixedValue(amount)} ${symbol}`;
};

/**
 * UtilityCostDetails component displaying illustrative examples (CASE 1 and CASE 2)
 * for how utility cost sharing works.
 * @param {UtilityCostDetailsProperties} properties - Component properties.
 * @returns {any} The rendered component.
 */
function UtilityCostDetails(properties: UtilityCostDetailsProperties) {
  const {
    energyCostType,
    energyCostEnabled,
    bearingOption,
    currency = 'SAR',
    partnerPercentage,
    cpoPercentage,
  } = properties;
  const { t } = useTranslation('revenue');

  const initialType: EnergyCostType =
    energyCostType || bearingOption || (energyCostEnabled ? 'partner' : 'none');

  const [selectedEnergyCostType, setSelectedEnergyCostType] =
    React.useState<EnergyCostType>(initialType);

  React.useEffect(() => {
    if (energyCostType) {
      setSelectedEnergyCostType(energyCostType);
    }
  }, [energyCostType]);

  const [totalCostInput, setTotalCostInput] = React.useState<string>('100');
  const [utilityCostInput, setUtilityCostInput] = React.useState<string>('20');

  // Derive partner and CPO percentages, ensuring they sum to 100%
  const effectivePartnerPct =
    partnerPercentage !== null && partnerPercentage !== undefined ? Number(partnerPercentage) : 30;
  const effectiveCpoPct =
    cpoPercentage !== null && cpoPercentage !== undefined
      ? Number(cpoPercentage)
      : Math.max(0, 100 - effectivePartnerPct);

  const parsedTotalCost = Math.max(0, Number(totalCostInput) || 0);
  const parsedUtilityCost = Math.max(0, Number(utilityCostInput) || 0);

  const isPartnerBearer = selectedEnergyCostType === 'partner';
  const isCpoBearer = selectedEnergyCostType === 'cpo';
  const isCostEnabled = isPartnerBearer || isCpoBearer;
  const utilityProviderCost = isCostEnabled ? parsedUtilityCost : 0;

  // For Case 1 & Case 2, utility cost is deducted first to obtain the shareable revenue
  const parsedShareableRevenue = isCostEnabled
    ? Math.max(0, parsedTotalCost - utilityProviderCost)
    : parsedTotalCost;

  // CASE 1: Utility cost added to Partner
  const partnerShareAmountCase1 =
    (effectivePartnerPct / 100) * parsedShareableRevenue + utilityProviderCost;
  const cpoShareAmountCase1 = (effectiveCpoPct / 100) * parsedShareableRevenue;

  // CASE 2: Utility cost added to CPO
  const partnerShareAmountCase2 = (effectivePartnerPct / 100) * parsedShareableRevenue;
  const cpoShareAmountCase2 =
    (effectiveCpoPct / 100) * parsedShareableRevenue + utilityProviderCost;

  // CASE 3: No energy cost added (None)
  const partnerShareAmountCase3 = (effectivePartnerPct / 100) * parsedTotalCost;
  const cpoShareAmountCase3 = (effectiveCpoPct / 100) * parsedTotalCost;

  /**
   * Handle change to the total session cost input
   * @param {string} _name - The field name.
   * @param {string | number} value - The input value.
   */
  const handleTotalCostChange = (_name: string, value: string | number) => {
    setTotalCostInput(String(value));
  };

  /**
   * Handle change to the utility provider cost input
   * @param {string} _name - The field name.
   * @param {string | number} value - The input value.
   */
  const handleUtilityCostChange = (_name: string, value: string | number) => {
    setUtilityCostInput(String(value));
  };

  /**
   * Handle radio change inside side panel
   * @param {string} _name - The field name.
   * @param {string | number} value - The selected value.
   */
  const handleBearerChange = (_name: string, value: string | number) => {
    setSelectedEnergyCostType(value as EnergyCostType);
  };

  return (
    <div className="utility-cost-details-wrapper">
      <div className="utility-cost-details-bearer-selector">
        <span className="utility-cost-details-bearer-label">
          {t('selectCostBearer', { defaultValue: 'Utility Cost Bearer' })}
        </span>
        <RadioButton
          name="sidePanelEnergyCostType"
          value={selectedEnergyCostType}
          list={[
            { label: t('partner', { defaultValue: 'Partner' }), value: 'partner' },
            { label: t('cpo', { defaultValue: 'CPO' }), value: 'cpo' },
            { label: t('none', { defaultValue: 'None' }), value: 'none' },
          ]}
          onChangeValue={handleBearerChange}
          fontSize="large"
        />
      </div>

      <div className="utility-cost-info-banner">
        <div className="utility-cost-info-banner__icon">
          <InfoIcon />
        </div>
        <p className="utility-cost-info-banner__text">
          <strong>{t('note', { defaultValue: 'Note:' })}</strong>{' '}
          {t('sampleDataDemonstrationNote', {
            defaultValue:
              'The data shown below is sample data illustrating how the calculation applies. Please note this is for demonstration and not real-time data.',
          })}
        </p>
      </div>

      <div className="utility-cost-details-container">
        {isPartnerBearer && (
          /* CASE 1: Utility Cost Added to Partner */
          <div className="utility-cost-card utility-cost-card--case-1 utility-cost-card--case-1-partner">
            <div className="utility-cost-card__header">
              <div className="utility-cost-card__title-row">
                <div className="utility-cost-card__title-group">
                  <span className="utility-cost-card__badge utility-cost-card__badge--case-1 utility-cost-card__badge--case-1-partner">
                    {t('case1', { defaultValue: 'CASE 1' })}
                  </span>
                  <span className="utility-cost-card__title utility-cost-card__title--case-1 utility-cost-card__title--case-1-partner">
                    {t('utilityCostAddedToPartner', {
                      defaultValue: 'Utility Cost Added (Partner)',
                    })}
                  </span>
                </div>
                <div className="utility-cost-card__subtitle">
                  {t('cdrTotalCostChargingSession', {
                    defaultValue: 'CDR Total Cost:',
                  })}{' '}
                  {formatCurrencyDisplay(parsedTotalCost, currency)} &rarr;{' '}
                  {t('utilityProviderCost', { defaultValue: 'Utility Provider Cost:' })}{' '}
                  {formatCurrencyDisplay(utilityProviderCost, currency)} &rarr;{' '}
                  {t('shareableRevenueLabel', { defaultValue: 'Shareable revenue:' })}{' '}
                  {formatCurrencyDisplay(parsedShareableRevenue, currency)}
                </div>
              </div>
            </div>

            <div className="utility-cost-card__content">
              {/* Total Session Cost Input */}
              <div className="utility-cost-card__item">
                <div className="utility-cost-card__item-left">
                  <div className="utility-cost-card__icon-box utility-cost-card__icon-box--neutral">
                    <CoinsIcon />
                  </div>
                  <div className="utility-cost-card__item-info">
                    <span className="utility-cost-card__item-name">
                      {t('totalSessionCost', { defaultValue: 'Total Session Cost' })}
                    </span>
                  </div>
                </div>
                <div className="utility-cost-card__input-box">
                  <CustomInput
                    name="totalCostInput"
                    type="number"
                    decimalLimit={2}
                    placeholder="100"
                    value={totalCostInput}
                    onChangeValue={handleTotalCostChange}
                    fontSize="small"
                    suffixText={currency}
                  />
                </div>
              </div>

              {/* Utility Provider Cost Input */}
              <div className="utility-cost-card__item">
                <div className="utility-cost-card__item-left">
                  <div className="utility-cost-card__icon-box utility-cost-card__icon-box--amber">
                    <UtilityPlugIcon />
                  </div>
                  <div className="utility-cost-card__item-info">
                    <span className="utility-cost-card__item-name">
                      {t('utilityProviderCost', { defaultValue: 'Utility Provider Cost' })}
                    </span>
                  </div>
                </div>
                <div className="utility-cost-card__input-box">
                  <CustomInput
                    name="utilityCostInput"
                    type="number"
                    decimalLimit={2}
                    placeholder="20"
                    value={utilityCostInput}
                    onChangeValue={handleUtilityCostChange}
                    fontSize="small"
                    suffixText={currency}
                  />
                </div>
              </div>

              {/* Shareable Revenue (Added before Partner Revenue) */}
              <div className="utility-cost-card__item">
                <div className="utility-cost-card__item-left">
                  <div className="utility-cost-card__icon-box utility-cost-card__icon-box--neutral">
                    <CoinsIcon />
                  </div>
                  <div className="utility-cost-card__item-info">
                    <span className="utility-cost-card__item-name">
                      {t('shareableRevenueLabel', {
                        defaultValue: 'Shareable Revenue',
                      })}
                    </span>
                    <span className="utility-cost-card__item-formula">
                      {formatCurrencyDisplay(parsedTotalCost, currency)} -{' '}
                      {formatCurrencyDisplay(utilityProviderCost, currency)}
                    </span>
                  </div>
                </div>
                <span className="utility-cost-card__item-value utility-cost-card__item-value--neutral">
                  {formatCurrencyDisplay(parsedShareableRevenue, currency)}
                </span>
              </div>

              {/* Partner Share */}
              <div className="utility-cost-card__item">
                <div className="utility-cost-card__item-left">
                  <div className="utility-cost-card__icon-box utility-cost-card__icon-box--green">
                    <PartnerUserIcon />
                  </div>
                  <div className="utility-cost-card__item-info">
                    <span className="utility-cost-card__item-name">
                      {t('partnerRevenueShareLabel', {
                        defaultValue: 'Partner Revenue Share',
                      })}
                    </span>
                    <span className="utility-cost-card__item-formula">
                      ({effectivePartnerPct}% {t('of', { defaultValue: 'of' })}{' '}
                      {formatCurrencyDisplay(parsedShareableRevenue, currency)}) +{' '}
                      {formatCurrencyDisplay(utilityProviderCost, currency)}
                    </span>
                  </div>
                </div>
                <span className="utility-cost-card__item-value utility-cost-card__item-value--green">
                  {formatCurrencyDisplay(partnerShareAmountCase1, currency)}
                </span>
              </div>

              {/* CPO Share */}
              <div className="utility-cost-card__item">
                <div className="utility-cost-card__item-left">
                  <div className="utility-cost-card__icon-box utility-cost-card__icon-box--blue">
                    <CpoBankIcon />
                  </div>
                  <div className="utility-cost-card__item-info">
                    <span className="utility-cost-card__item-name">
                      {t('cpoRevenueShareLabel', { defaultValue: 'CPO Revenue Share' })}
                    </span>
                    <span className="utility-cost-card__item-formula">
                      ({effectiveCpoPct}% {t('of', { defaultValue: 'of' })}{' '}
                      {formatCurrencyDisplay(parsedShareableRevenue, currency)})
                    </span>
                  </div>
                </div>
                <span className="utility-cost-card__item-value utility-cost-card__item-value--blue">
                  {formatCurrencyDisplay(cpoShareAmountCase1, currency)}
                </span>
              </div>
            </div>
          </div>
        )}

        {isCpoBearer && (
          /* CASE 2: Utility Cost Added to CPO */
          <div className="utility-cost-card utility-cost-card--case-2 utility-cost-card--case-2-cpo">
            <div className="utility-cost-card__header">
              <div className="utility-cost-card__title-row">
                <div className="utility-cost-card__title-group">
                  <span className="utility-cost-card__badge utility-cost-card__badge--case-2 utility-cost-card__badge--case-2-cpo">
                    {t('case2', { defaultValue: 'CASE 2' })}
                  </span>
                  <span className="utility-cost-card__title utility-cost-card__title--case-2 utility-cost-card__title--case-2-cpo">
                    {t('utilityCostAddedToCpo', { defaultValue: 'Utility Cost Added (CPO)' })}
                  </span>
                </div>
                <div className="utility-cost-card__subtitle">
                  {t('cdrTotalCostChargingSession', {
                    defaultValue: 'CDR Total Cost:',
                  })}{' '}
                  {formatCurrencyDisplay(parsedTotalCost, currency)} &rarr;{' '}
                  {t('utilityProviderCost', { defaultValue: 'Utility Provider Cost:' })}{' '}
                  {formatCurrencyDisplay(utilityProviderCost, currency)} &rarr;{' '}
                  {t('shareableRevenueLabel', { defaultValue: 'Shareable revenue:' })}{' '}
                  {formatCurrencyDisplay(parsedShareableRevenue, currency)}
                </div>
              </div>
            </div>

            <div className="utility-cost-card__content">
              {/* Total Session Cost Input */}
              <div className="utility-cost-card__item">
                <div className="utility-cost-card__item-left">
                  <div className="utility-cost-card__icon-box utility-cost-card__icon-box--neutral">
                    <CoinsIcon />
                  </div>
                  <div className="utility-cost-card__item-info">
                    <span className="utility-cost-card__item-name">
                      {t('totalSessionCost', { defaultValue: 'Total Session Cost' })}
                    </span>
                  </div>
                </div>
                <div className="utility-cost-card__input-box">
                  <CustomInput
                    name="totalCostInput"
                    type="number"
                    decimalLimit={2}
                    placeholder="100"
                    value={totalCostInput}
                    onChangeValue={handleTotalCostChange}
                    fontSize="small"
                    suffixText={currency}
                  />
                </div>
              </div>

              {/* Utility Provider Cost Input */}
              <div className="utility-cost-card__item">
                <div className="utility-cost-card__item-left">
                  <div className="utility-cost-card__icon-box utility-cost-card__icon-box--amber">
                    <UtilityPlugIcon />
                  </div>
                  <div className="utility-cost-card__item-info">
                    <span className="utility-cost-card__item-name">
                      {t('utilityProviderCost', { defaultValue: 'Utility Provider Cost' })}
                    </span>
                  </div>
                </div>
                <div className="utility-cost-card__input-box">
                  <CustomInput
                    name="utilityCostInput"
                    type="number"
                    decimalLimit={2}
                    placeholder="20"
                    value={utilityCostInput}
                    onChangeValue={handleUtilityCostChange}
                    fontSize="small"
                    suffixText={currency}
                  />
                </div>
              </div>

              {/* Shareable Revenue (Added before Partner Revenue) */}
              <div className="utility-cost-card__item">
                <div className="utility-cost-card__item-left">
                  <div className="utility-cost-card__icon-box utility-cost-card__icon-box--neutral">
                    <CoinsIcon />
                  </div>
                  <div className="utility-cost-card__item-info">
                    <span className="utility-cost-card__item-name">
                      {t('shareableRevenueLabel', {
                        defaultValue: 'Shareable Revenue',
                      })}
                    </span>
                    <span className="utility-cost-card__item-formula">
                      {formatCurrencyDisplay(parsedTotalCost, currency)} -{' '}
                      {formatCurrencyDisplay(utilityProviderCost, currency)}
                    </span>
                  </div>
                </div>
                <span className="utility-cost-card__item-value utility-cost-card__item-value--neutral">
                  {formatCurrencyDisplay(parsedShareableRevenue, currency)}
                </span>
              </div>

              {/* Partner Share */}
              <div className="utility-cost-card__item">
                <div className="utility-cost-card__item-left">
                  <div className="utility-cost-card__icon-box utility-cost-card__icon-box--green">
                    <PartnerUserIcon />
                  </div>
                  <div className="utility-cost-card__item-info">
                    <span className="utility-cost-card__item-name">
                      {t('partnerRevenueShareLabel', {
                        defaultValue: 'Partner Revenue Share',
                      })}
                    </span>
                    <span className="utility-cost-card__item-formula">
                      ({effectivePartnerPct}% {t('of', { defaultValue: 'of' })}{' '}
                      {formatCurrencyDisplay(parsedShareableRevenue, currency)})
                    </span>
                  </div>
                </div>
                <span className="utility-cost-card__item-value utility-cost-card__item-value--green">
                  {formatCurrencyDisplay(partnerShareAmountCase2, currency)}
                </span>
              </div>

              {/* CPO Share */}
              <div className="utility-cost-card__item">
                <div className="utility-cost-card__item-left">
                  <div className="utility-cost-card__icon-box utility-cost-card__icon-box--blue">
                    <CpoBankIcon />
                  </div>
                  <div className="utility-cost-card__item-info">
                    <span className="utility-cost-card__item-name">
                      {t('cpoRevenueShareLabel', { defaultValue: 'CPO Revenue Share' })}
                    </span>
                    <span className="utility-cost-card__item-formula">
                      ({effectiveCpoPct}% {t('of', { defaultValue: 'of' })}{' '}
                      {formatCurrencyDisplay(parsedShareableRevenue, currency)}) +{' '}
                      {formatCurrencyDisplay(utilityProviderCost, currency)}
                    </span>
                  </div>
                </div>
                <span className="utility-cost-card__item-value utility-cost-card__item-value--blue">
                  {formatCurrencyDisplay(cpoShareAmountCase2, currency)}
                </span>
              </div>
            </div>
          </div>
        )}

        {!isCostEnabled && (
          /* CASE 3: Utility Cost Not Added (None) */
          <div className="utility-cost-card utility-cost-card--case-3">
            <div className="utility-cost-card__header">
              <div className="utility-cost-card__title-row">
                <div className="utility-cost-card__title-group">
                  <span className="utility-cost-card__badge utility-cost-card__badge--case-3">
                    {t('case3', { defaultValue: 'CASE 3' })}
                  </span>
                  <span className="utility-cost-card__title utility-cost-card__title--case-3">
                    {t('utilityCostNotAdded', { defaultValue: 'Utility Cost Not Added' })}
                  </span>
                </div>
                <div className="utility-cost-card__subtitle">
                  {t('cdrTotalCostChargingSession', {
                    defaultValue: 'CDR Total Cost:',
                  })}{' '}
                  {formatCurrencyDisplay(parsedTotalCost, currency)} &rarr;{' '}
                  {t('shareableRevenueLabel', { defaultValue: 'Shareable revenue:' })}{' '}
                  {formatCurrencyDisplay(parsedTotalCost, currency)}
                </div>
              </div>
            </div>

            <div className="utility-cost-card__content">
              {/* Total Session Cost Input */}
              <div className="utility-cost-card__item">
                <div className="utility-cost-card__item-left">
                  <div className="utility-cost-card__icon-box utility-cost-card__icon-box--neutral">
                    <CoinsIcon />
                  </div>
                  <div className="utility-cost-card__item-info">
                    <span className="utility-cost-card__item-name">
                      {t('totalSessionCost', { defaultValue: 'Total Session Cost' })}
                    </span>
                  </div>
                </div>
                <div className="utility-cost-card__input-box">
                  <CustomInput
                    name="totalCostInput"
                    type="number"
                    decimalLimit={2}
                    placeholder="100"
                    value={totalCostInput}
                    onChangeValue={handleTotalCostChange}
                    fontSize="small"
                    suffixText={currency}
                  />
                </div>
              </div>

              {/* Shareable Revenue (Added before Partner Revenue) */}
              <div className="utility-cost-card__item">
                <div className="utility-cost-card__item-left">
                  <div className="utility-cost-card__icon-box utility-cost-card__icon-box--neutral">
                    <CoinsIcon />
                  </div>
                  <div className="utility-cost-card__item-info">
                    <span className="utility-cost-card__item-name">
                      {t('shareableRevenueLabel', {
                        defaultValue: 'Shareable Revenue',
                      })}
                    </span>
                    <span className="utility-cost-card__item-formula">
                      {formatCurrencyDisplay(parsedTotalCost, currency)}
                    </span>
                  </div>
                </div>
                <span className="utility-cost-card__item-value utility-cost-card__item-value--neutral">
                  {formatCurrencyDisplay(parsedTotalCost, currency)}
                </span>
              </div>

              {/* Partner Share */}
              <div className="utility-cost-card__item">
                <div className="utility-cost-card__item-left">
                  <div className="utility-cost-card__icon-box utility-cost-card__icon-box--green">
                    <PartnerUserIcon />
                  </div>
                  <div className="utility-cost-card__item-info">
                    <span className="utility-cost-card__item-name">
                      {t('partnerRevenueShareLabel', {
                        defaultValue: 'Partner Revenue Share',
                      })}
                    </span>
                    <span className="utility-cost-card__item-formula">
                      ({effectivePartnerPct}% {t('of', { defaultValue: 'of' })}{' '}
                      {formatCurrencyDisplay(parsedTotalCost, currency)})
                    </span>
                  </div>
                </div>
                <span className="utility-cost-card__item-value utility-cost-card__item-value--green">
                  {formatCurrencyDisplay(partnerShareAmountCase3, currency)}
                </span>
              </div>

              {/* CPO Share */}
              <div className="utility-cost-card__item">
                <div className="utility-cost-card__item-left">
                  <div className="utility-cost-card__icon-box utility-cost-card__icon-box--blue">
                    <CpoBankIcon />
                  </div>
                  <div className="utility-cost-card__item-info">
                    <span className="utility-cost-card__item-name">
                      {t('cpoRevenueShareLabel', { defaultValue: 'CPO Revenue Share' })}
                    </span>
                    <span className="utility-cost-card__item-formula">
                      ({effectiveCpoPct}% {t('of', { defaultValue: 'of' })}{' '}
                      {formatCurrencyDisplay(parsedTotalCost, currency)})
                    </span>
                  </div>
                </div>
                <span className="utility-cost-card__item-value utility-cost-card__item-value--blue">
                  {formatCurrencyDisplay(cpoShareAmountCase3, currency)}
                </span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default UtilityCostDetails;
